import { Slot } from "@radix-ui/react-slot";

export type DatadogPrivacyLevel =
  | "allow"
  | "mask"
  | "hidden"
  | "mask-user-input";

export interface DatadogPrivacyProps {
  /**
   * The privacy level applied to the underlying element.
   * @see https://docs.datadoghq.com/session_replay/browser/privacy_options/#override-an-html-element
   * @default 'allow'
   */
  level?: DatadogPrivacyLevel;
  /**
   * The element to apply the privacy level to. Must be a single React element
   * (not a string or fragment), since the component spreads `data-dd-privacy`
   * onto the underlying element rather than rendering its own wrapper.
   */
  children: React.ReactElement;
}

/**
 * With `defaultPrivacyLevel="mask"`, Datadog masks all DOM content in session
 * replays by default. `DatadogPrivacy` overrides that behavior for a single
 * element — most commonly to opt non-sensitive UI back in for debugging via
 * `level="allow"` — without introducing an extra wrapper node.
 *
 * @example
 * <DatadogPrivacy level="allow">
 *   <span>{statusLabel}</span>
 * </DatadogPrivacy>
 */
export const DatadogPrivacy = ({
  children,
  level = "allow",
}: DatadogPrivacyProps) => {
  return <Slot data-dd-privacy={level}>{children}</Slot>;
};
